// 2026-09-07 신규 — 등급제 배포 안내(첫 로그인 전면 안내 · 홈 상단 띠) 상태 조회.
// RPC get_tier_rollout_notice_state(p_email) 한 번으로 「이 사람에게 무엇을, 봤는지」를 받는다.
//   { acked, acked_at, legacy, cutoff, effective_from, grace, tier, status, monthly_fee, exempt,
//     channel_count, channel_limit, is_rights_holder, role }
// 기획: docs/ops/tier-rollout-communication-plan.md §3·§5. 세그먼트·문구는 src/lib/tierRolloutNotice.js 가 계산한다.
// useTierBillingState 와 같은 계약: 모듈 캐시 5분 + 진행 중 요청 합치기. 실패하면 null — 안내를 띄우지 않는다
// (안내가 앱을 막으면 안 된다 · PolicyConsentGate 와 같은 원칙).
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { normalizeRolloutNoticeState } from "../lib/tierRolloutNotice";

const TTL_MS = 5 * 60 * 1000;
let cache = null; // { email, at, state }
let inflight = null; // { email, promise }

export function _resetTierRolloutNoticeCache() {
  cache = null;
  inflight = null;
}

async function fetchState(email, { force = false } = {}) {
  if (!force && cache && cache.email === email && Date.now() - cache.at < TTL_MS) return cache.state;
  if (inflight && inflight.email === email) return inflight.promise;
  const promise = (async () => {
    const { data, error } = await supabase.rpc("get_tier_rollout_notice_state", { p_email: email });
    if (error) throw error;
    const state = normalizeRolloutNoticeState(data);
    cache = { email, at: Date.now(), state };
    inflight = null;
    return state;
  })().catch((err) => {
    inflight = null;
    throw err;
  });
  inflight = { email, promise };
  return promise;
}

/** 확인(「확인했어요」) — 서버에 1회 기록. 성공하면 캐시도 acked 로 바꿔 다른 화면(띠)이 바로 따라온다. */
export async function ackTierRolloutNotice({ segment, variant = null } = {}) {
  const { data, error } = await supabase.rpc("ack_tier_rollout_notice", { p_segment: segment, p_variant: variant });
  if (error) throw error;
  if (cache?.state) cache = { ...cache, state: { ...cache.state, acked: true, ackedAt: data?.acked_at || new Date().toISOString() } };
  return data;
}

export default function useTierRolloutNotice(email, { enabled = true } = {}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled && email));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || !email) {
      setState(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchState(email, { force: tick > 0 })
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("등급제 안내 상태 조회 실패:", err?.message || err);
          setState(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, enabled, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const markAcked = useCallback(() => setState((s) => (s ? { ...s, acked: true } : s)), []);
  return { notice: state, loading, refresh, markAcked };
}
